import React, { Fragment } from 'react';

import { useFreecodeContextData, useFreecodeContextRun } from 'hooks/useContext';
import { categories } from 'constants/tests';
import { LanguageHeader } from './styles';
import { CreatorSection, NumberInput, SectionHeader, Label, InputContainer } from '../styles';

export default function FreecodeLevelSettings() {
  const { test, testImpl, setTestImpl, languageData } = useFreecodeContextData();
  const { levelToRun, runHiddenRef, selectLevelToRun, runResponse } = useFreecodeContextRun();

  const handleConfigLevelsUpdate = (parameter, value) =>
    setTestImpl((oldTestImpl) => ({
      ...oldTestImpl,
      configLevels: { ...oldTestImpl.configLevels, [parameter]: value },
    }));

  const handleConfigLevelsNumberUpdate = (parameter, value) => {
    if (isNaN(value)) return;
    if (!runResponse) {
      // Adjust canvas display if using peek data
      if (parameter === 'numPublic' && !runHiddenRef.current && value <= levelToRun) selectLevelToRun(value - 1);
      if (parameter === 'numPublic' && runHiddenRef.current && value + testImpl.configLevels.numHidden <= levelToRun)
        selectLevelToRun(value + testImpl.configLevels.numHidden - 1);
      if (parameter === 'numHidden' && testImpl.configLevels.numPublic + value <= levelToRun)
        selectLevelToRun(testImpl.configLevels.numPublic + value - 1);
    }
    handleConfigLevelsUpdate(parameter, value);
  };

  const handlePerformanceScoreChange = (language, index, value) => {
    let goodPerformanceScores = { ...testImpl.configLevels.goodPerformanceScores };
    if (!goodPerformanceScores[language]) goodPerformanceScores[language] = [];
    while (goodPerformanceScores[language].length < index + 1) {
      goodPerformanceScores[language].push(1000);
    }
    goodPerformanceScores[language][index] = value;
    handleConfigLevelsUpdate('goodPerformanceScores', goodPerformanceScores);
  };

  const renderPerformanceScoresSection = () => {
    return (
      <CreatorSection>
        <SectionHeader>Good performance scores</SectionHeader>
        {Object.keys(languageData || {}).map((language, languageIndex) => (
          <Fragment key={languageIndex}>
            <LanguageHeader>{languageData[language]?.SimpleName || language}</LanguageHeader>
            {[...Array(testImpl.configLevels.numPerformance).keys()].map((perfKey) => (
              <InputContainer key={perfKey}>
                <Label noWrap>Performance score {perfKey + 1}:</Label>
                <NumberInput
                  value={testImpl.configLevels.goodPerformanceScores?.[language]?.[perfKey]}
                  onChange={(e) => handlePerformanceScoreChange(language, perfKey, Math.max(1, e.target.value))}
                />
              </InputContainer>
            ))}
          </Fragment>
        ))}
      </CreatorSection>
    );
  };

  const manageNumPerformance = (target) => {
    let goodPerformanceScores = { ...testImpl.configLevels.goodPerformanceScores };
    for (const language of Object.keys(languageData || {})) {
      if (!goodPerformanceScores[language]) goodPerformanceScores[language] = [];
      while (goodPerformanceScores[language].length < target + 1) {
        goodPerformanceScores[language].push(1000);
      }
      // If the number of performance levels is reduced, remove goodPerformanceScores
      if (goodPerformanceScores[language].length > target) {
        goodPerformanceScores[language] = goodPerformanceScores[language].splice(0, target);
      }
    }
    handleConfigLevelsUpdate('goodPerformanceScores', goodPerformanceScores);
    handleConfigLevelsNumberUpdate('numPerformance', target);
  };

  return (
    <>
      <CreatorSection>
        <SectionHeader>Level Settings</SectionHeader>
        <InputContainer>
          <Label noWrap>Number of public levels:</Label>
          <NumberInput
            value={testImpl.configLevels.numPublic}
            onChange={(e) => handleConfigLevelsNumberUpdate('numPublic', Math.max(1, e.target.value))}
          ></NumberInput>
        </InputContainer>
        <InputContainer>
          <Label noWrap>Number of hidden levels:</Label>
          <NumberInput
            value={testImpl.configLevels.numHidden}
            onChange={(e) => handleConfigLevelsNumberUpdate('numHidden', Math.max(0, e.target.value))}
          ></NumberInput>
        </InputContainer>
        {test.category === categories.CODE.category && (
          <InputContainer>
            <Label noWrap>Number of performance levels:</Label>
            <NumberInput
              value={testImpl.configLevels.numPerformance}
              onChange={(e) => manageNumPerformance(Math.max(0, e.target.value))}
            ></NumberInput>
          </InputContainer>
        )}
      </CreatorSection>
      {test.category === categories.CODE.category &&
        testImpl.configLevels?.numPerformance > 0 &&
        renderPerformanceScoresSection()}
    </>
  );
}
